import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { usePesquisaClienteListaViewModel } from "../../viewModels/pesquisa_cliente_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GeneratePesquisaClienteListaColumnDefinition } from "./columns";
import { SatiscacaoClienteLista } from "../../../domain/models/satisfacao_cliente_lista";
import { SatisfacaoClienteModal } from "./components/satisfacao_cliente_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";

import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * PesquisaClienteLista
 */

export const PesquisaClienteLista = () => {
  const viewModel = usePesquisaClienteListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GeneratePesquisaClienteListaColumnDefinition({
      onActionEditar: viewModel.handleSatisfacaoClienteModalOpenState,
      onActionDelete: viewModel.handleSatisfacaoClienteDeleteModalOpenState,
      onActionVisualizar: viewModel.handleSatisfacaoClienteVisualizar,
    });
  }, [
    viewModel.handleSatisfacaoClienteModalOpenState,
    viewModel.handleSatisfacaoClienteDeleteModalOpenState,
    viewModel.handleSatisfacaoClienteVisualizar,
  ]);

  const pinnedColumns = useMemo(() => {
    return viewModel.pesquisaClientes.length === 0
      ? undefined
      : { left: [], right: ['idSatisfacaoCliente', 'visualizar'] }
  }, [
    viewModel.pesquisaClientes,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    viewModel.handleSatisfacaoClienteModalOpenState(null, true);
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-6 flex-col py-10 px-4 mb-16">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Entrega de Obras
          </Link>
          <Typography className="text-primary-green">
            Satisfação do Cliente
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Satisfação do Cliente</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex gap-2">
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload}
                />
              </div>
            ) : (
              <>
                <Header
                  preencher={viewModel.filters.preencher}
                  centroCustos={viewModel.filters.centroCustos}
                  status={viewModel.filters.status}
                  clientes={viewModel.filters.clientes}
                  pesquisaClientes={viewModel.filters.pesquisaCliente}
                  centroCustosDropdown={viewModel.centroCustosDropdown}
                  clientesDropdown={viewModel.clientesDropdown}
                  onPreencherChange={viewModel.handlePreencherChange}
                  onCentroCustosChange={viewModel.handleCentroCustosChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClientesChange={viewModel.handleClientesChange}
                  onPesquisaClienteChange={viewModel.handlePesquisaClienteChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="pesquisa_cliente_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    getRowId={(row: SatiscacaoClienteLista) => row.idSatisfacaoCliente}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.pesquisaClientes}
                    rowCount={viewModel.pesquisaClientes.length}
                    pinnedColumns={pinnedColumns}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <div className="flex flex-row justify-between items-center">
                    <span className="font-bold text-black dark:text-white">
                      Média Geral: {
                        new Intl.NumberFormat("pt-BR", {
                          style: 'percent',
                          minimumFractionDigits: 2,
                        }).format(viewModel.resultadoMedio)
                      }
                    </span>

                    <TablePagination
                      component="div"
                      count={viewModel.pagination.totalItems}
                      page={viewModel.pagination.page - 1}
                      onPageChange={(_, p) => viewModel.handlePageChange(p)}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      rowsPerPage={viewModel.pagination.rowsPerPage}
                      onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                    />
                  </div>

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <SatisfacaoClienteModal
        open={viewModel.safistacaoClienteModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedSatisfacaoCliente={viewModel.selectedPesquisaCliente}
        centroCustosDropdown={viewModel.centroCustosDropdown}
        clientesDropdown={viewModel.clientesDropdown}
        onClose={() => viewModel.handleSatisfacaoClienteModalOpenState(null, false)}
        onSubmit={viewModel.handleSatisfacaoClienteSubmit}
        onOpenChangeHistory={viewModel.handleOpenChangeHistory}
      />

      <DeleteDialog
        open={viewModel.safistacaoClienteDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleSatisfacaoClienteDeleteModalOpenState(null, false)}
        onDelete={() => viewModel.handleSatisfacaoClienteDeleteSubmit()}
      />

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />

    </>
  );
}